import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, ComposedChart, ReferenceDot,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Scatter
} from 'recharts';
import trendsRaw from '../data/program_trends.json';
import { fmt, COLORS, PROGRAM_EVENTS } from '../utils';
import ChartCard from './ChartCard';
import KPITooltip, { TOOLTIPS } from './KPITooltip';

// ── Static year narratives ──────────────────────────────────────────────────
const YEAR_NARRATIVES = {
  2013: "The program launched with 220 ACOs — nearly all in Track 1 (one-sided risk). Quality scoring was binary. Only about 1 in 4 ACOs generated enough savings to earn shared savings payments.",
  2014: "Participation grew 51% to 333 ACOs. A new benchmark methodology with BY1/BY2/BY3 weighting was introduced. The average savings rate turned slightly negative as the program expanded.",
  2015: "392 ACOs participated. The program began tracking condition-specific admissions (CHF, COPD) and added new outpatient payment categories.",
  2016: "A major expansion: quality measure reporting grew to 42 individual measures (ACO1–ACO42). Agreement type tracking began. Track 3 launched with full two-sided risk.",
  2017: "472 ACOs participated — the largest pre-Pathways cohort. Disease adjustment, AIM model flags, and prior savings adjustments were introduced.",
  2018: "Enrollment peaked at 548 ACOs. Track 1+ launched. SNF 3-day rule waivers became available. ACO_ID replaced legacy ACO_Num identifiers.",
  2019: "A pivotal year: Pathways to Success launched July 1, creating two cohorts. Legacy-track ACOs (475) completed a full year; 205 new Pathways ACOs completed a 6-month period. Over 50% of ACOs generated savings for the first time.",
  2020: "COVID-19 dramatically reduced utilization — admissions fell 15%, ED visits dropped 18%. Despite this, ACOs earned a record $2.3B as expenditures fell below benchmarks. DisAffQual applied universally.",
  2021: "CAHPS patient experience measures added. Sliding-scale quality scoring replaced the all-or-nothing threshold, making savings access more equitable for lower-performing ACOs.",
  2022: "The Current_Track field replaced individual track flag columns. Net savings reached $2.5B. ACO_State field dropped from PUF.",
  2023: "Record $3.1B in earned savings — the program's strongest year to date. 69% of ACOs generated savings. FinalLossRate field introduced for two-sided risk ACOs.",
  2024: "The largest PUF schema in program history (189 fields). Medicare CQM collection introduced. 75% of ACOs generated savings, with total earned savings reaching $4.1B — an all-time program record.",
};

// ── Track label mapping ─────────────────────────────────────────────────────
const TRACK_LABEL_MAP = {
  'Track_1': 'Track 1', 'Track_2': 'Track 2', 'Track_3': 'Track 3',
  'Track_1Plus': 'Track 1+',
  'A': 'Basic A', 'B': 'Basic B', 'C': 'Basic C', 'D': 'Basic D', 'E': 'Basic E', 'EN': 'Enhanced',
  // Legacy full-name variants (2021 PUF used BASIC_X and ENHANCED)
  'BASIC_A': 'Basic A', 'BASIC_B': 'Basic B', 'BASIC_C': 'Basic C',
  'BASIC_D': 'Basic D', 'BASIC_E': 'Basic E', 'ENHANCED': 'Enhanced',
};
const TRACK_COLORS_DETAIL = {
  'Track 1': '#335B74', 'Track 2': '#d4860a', 'Track 3': '#2e7d4f',
  'Track 1+': '#6b4ca8',
  'Basic A': '#8bc4e0', 'Basic B': '#42BA97', 'Basic C': '#1D9E75',
  'Basic D': '#0f6e56', 'Basic E': '#085041', 'Enhanced': '#2e7d4f',
};
const RISK_COLORS = {
  'One-Sided': COLORS.blue,
  'Two-Sided': COLORS.teal,
};

// ── Tooltips ────────────────────────────────────────────────────────────────
const SavingsComboTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color || p.fill }}>{p.name}</span>
          <span className="custom-tooltip-val">{fmt.dollars(p.value * 1e6, 2)}</span>
        </div>
      ))}
    </div>
  );
};

const PctTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="custom-tooltip-val">{Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
};

const SpendTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload?.length) return null;
  const formatVal = (v) => {
    if (v == null) return '—';
    if (mode === 'aggregate') return fmt.dollars(v * 1e9, 2);
    if (mode === 'pbpm') return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return fmt.dollars(v, 2);
  };
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="custom-tooltip-val">{formatVal(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const UtilTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="custom-tooltip-val">{Number(p.value).toFixed(1)} per 1K</span>
        </div>
      ))}
    </div>
  );
};

// ── Toggle button group ─────────────────────────────────────────────────────
function ToggleGroup({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', overflow: 'hidden', flexShrink: 0 }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          style={{
            padding: '4px 12px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer',
            border: 'none', borderRight: '1px solid var(--gray-300)',
            background: value === opt.value ? 'var(--navy)' : 'white',
            color: value === opt.value ? 'white' : 'var(--gray-600)',
            fontWeight: value === opt.value ? 600 : 400,
          }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function ProgramOverview({ perfData, navigateToACO, pinnedACO }) {
  const [trackMode, setTrackMode]   = useState('risk');    // 'detail' | 'risk'
  const [spendMode, setSpendMode]   = useState('pbpm');    // 'aggregate' | 'percapita' | 'pbpm'

  const legacy  = useMemo(() => trendsRaw.filter(d => d.is_midyear === 0), []);
  const midyear = useMemo(() => trendsRaw.filter(d => d.is_midyear === 1), []);

  // ── All-time aggregates from perfData ──
  const allTimeStats = useMemo(() => {
    if (!perfData) return null;
    const recs = perfData.filter(r => r.is_midyear === 0);
    const uniqueACOs   = new Set(recs.map(r => r.aco_id)).size;
    const earnedOnce   = new Set(recs.filter(r => (r.earn_save_loss||0) > 0).map(r => r.aco_id)).size;
    const totalEarned  = recs.reduce((s,r) => s + (r.earn_save_loss||0), 0);
    const totalGen     = recs.filter(r => r.gen_save_loss != null).reduce((s,r) => s + (r.gen_save_loss||0), 0);
    const cmsRetained  = totalGen - totalEarned;
    const totalBenes   = legacy.reduce((s,d) => s + (d.total_n_ab||0), 0);
    return { uniqueACOs, earnedOnce, pctEarnedOnce: (earnedOnce/uniqueACOs*100).toFixed(1), totalEarned, totalGen, cmsRetained, totalBenes };
  }, [perfData, legacy]);

  // ── Main chart data ──
  const chartData = useMemo(() => legacy.map(d => {
    // Program-level savings rate from perfData
    const acos = perfData?.filter(r => r.performance_year === d.performance_year && r.is_midyear === 0) || [];
    const totBnchmk = acos.reduce((s,r) => s + (r.abtot_bnchmk||0), 0);
    const totExp    = acos.reduce((s,r) => s + (r.abtot_exp||0), 0);
    const progSavRate = totBnchmk > 0 ? (totBnchmk - totExp) / totBnchmk * 100 : null;
    const pctOwing  = d.n_acos > 0 ? (acos.filter(r => (r.earn_save_loss||0) < 0).length / d.n_acos * 100) : 0;

    // Savings split in $M
    const earnM = d.total_earn_save_loss != null ? d.total_earn_save_loss / 1e6 : null;
    const genM  = d.total_gen_save_loss  != null ? d.total_gen_save_loss  / 1e6 : null;
    const cmsM  = (genM != null && earnM != null) ? genM - earnM : null;

    // Net ED (non-hospitalization)
    const avgNetED = acos.length > 0
      ? acos.reduce((s,r) => s + ((r.p_edv_vis||0) - (r.p_edv_vis_hosp||0)), 0) / acos.length : null;

    // EM visits by type
    const avgPcpEM  = acos.length > 0 ? acos.reduce((s,r) => s + (r.p_em_pcp_vis||0), 0) / acos.length : null;
    const avgSpEM   = acos.length > 0 ? acos.reduce((s,r) => s + (r.p_em_sp_vis||0), 0) / acos.length : null;
    const avgNurse  = acos.length > 0 ? acos.reduce((s,r) => s + (r.p_nurse_vis||0), 0) / acos.length : null;

    // Spend modes
    const bnchmkB   = totBnchmk / 1e9;
    const expB      = totExp / 1e9;
    const totBenYear = acos.reduce((s,r) => s + (r.n_ab_year_py||0), 0);
    const bnchmkPC  = totBenYear > 0 ? totBnchmk / totBenYear : null;
    const expPC     = totBenYear > 0 ? totExp / totBenYear : null;

    return {
      performance_year: d.performance_year,
      n_acos:   d.n_acos,
      total_n_ab: d.total_n_ab,
      pct_earning_savings: d.pct_earning_savings,
      pct_owing: pctOwing > 0 ? pctOwing : null,
      prog_sav_rate: progSavRate,
      earn_M: earnM, gen_M: genM, cms_M: cmsM,
      avg_adm: d.avg_adm,
      avg_net_ed: avgNetED,
      avg_p_snf_adm: d.avg_p_snf_adm,
      avg_pcp_em: avgPcpEM, avg_sp_em: avgSpEM, avg_nurse: avgNurse,
      avg_benchmark_pbpm: d.avg_benchmark_pbpm,
      avg_expense_pbpm: d.avg_expense_pbpm,
      bnchmk_B: bnchmkB, exp_B: expB,
      bnchmk_pc: bnchmkPC, exp_pc: expPC,
      avg_qual: d.avg_qual_score,
      pct_met_qps: d.pct_met_qps != null ? d.pct_met_qps * 100 : null,
    };
  }), [legacy, perfData]);

  // ── Track distribution data ──
  const trackDetailData = useMemo(() => {
    if (!perfData) return [];
    return legacy.map(d => {
      const acos = perfData.filter(r => r.performance_year === d.performance_year && r.is_midyear === 0);
      const counts = {};
      acos.forEach(r => {
        const raw = (r.current_track || '').trim();
        const label = TRACK_LABEL_MAP[raw] || raw;
        counts[label] = (counts[label] || 0) + 1;
      });
      return { year: d.performance_year, ...counts };
    });
  }, [perfData, legacy]);

  const trackRiskData = useMemo(() => {
    if (!perfData) return [];
    return legacy.map(d => {
      const acos = perfData.filter(r => r.performance_year === d.performance_year && r.is_midyear === 0);
      const oneSided  = acos.filter(r => {
        const t = (r.current_track||'').trim().toUpperCase();
        return ['A','B','TRACK_1'].includes(t) || t.includes('BASIC_A') || t.includes('BASIC_B') || t === 'TRACK_1';
      }).length;
      return { year: d.performance_year, 'One-Sided': oneSided, 'Two-Sided': d.n_acos - oneSided };
    });
  }, [perfData, legacy]);

  const allDetailTracks = useMemo(() => {
    const keys = new Set();
    trackDetailData.forEach(d => Object.keys(d).filter(k => k !== 'year').forEach(k => keys.add(k)));
    return [...keys];
  }, [trackDetailData]);

  // Latest / prior year
  const latest = legacy[legacy.length - 1];
  const prior  = legacy[legacy.length - 2];

  // Program-level savings rate for latest year
  // Pinned ACO year-by-year data for overlay on program charts
  const pinnedChartData = useMemo(() => {
    if (!pinnedACO || !perfData) return [];
    return perfData
      .filter(r => r.aco_id === pinnedACO.aco_id && r.is_midyear === 0)
      .sort((a, b) => a.performance_year - b.performance_year)
      .map(r => ({
        performance_year: r.performance_year,
        sav_rate: r.sav_rate,
        earn_save_loss: r.earn_save_loss,
        qual_score: r.qual_score,
        expense_pbpm: r.expense_pbpm,
      }));
  }, [pinnedACO, perfData]);

  const latestProgRate = chartData[chartData.length - 1]?.prog_sav_rate;
  const priorProgRate  = chartData[chartData.length - 2]?.prog_sav_rate;

  // Spend mode helpers
  const spendKeys = {
    aggregate:  { bnchmk: 'bnchmk_B',           exp: 'exp_B',           yFmt: v => `$${v?.toFixed(1)}B`, label: 'Aggregate ($B)' },
    percapita:  { bnchmk: 'bnchmk_pc',           exp: 'exp_pc',          yFmt: v => `$${Math.round(v||0).toLocaleString()}`, label: 'Per Beneficiary' },
    pbpm:       { bnchmk: 'avg_benchmark_pbpm',  exp: 'avg_expense_pbpm',yFmt: v => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—', label: 'PBPM' },
  };
  const sk = spendKeys[spendMode];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── All-time aggregate header ── */}
      {allTimeStats && (
        <div style={{ background: 'var(--navy)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
            letterSpacing: '0.1em', marginBottom: 14 }}>
            All-Time Program Summary · PY 2013 – 2024
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 20 }}>
            {[
              { label: 'Unique ACOs participated',            value: fmt.num(allTimeStats.uniqueACOs) },
              { label: 'ACOs that earned savings (ever)',     value: `${allTimeStats.earnedOnce} (${allTimeStats.pctEarnedOnce}%)` },
              { label: 'Total earned savings (ACO share)',    value: fmt.dollars(allTimeStats.totalEarned, 2) },
              { label: 'CMS retained (gen − earned)',         value: fmt.dollars(allTimeStats.cmsRetained, 2) },
              { label: 'Total generated savings',            value: fmt.dollars(allTimeStats.totalGen, 2) },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: '1.9rem', fontWeight: 700, color: 'var(--teal)', lineHeight: 1.1 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Most recent year KPI row ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase',
          letterSpacing: '0.08em', marginBottom: 10 }}>
          Most Recent Performance Year (PY {latest?.performance_year})
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          {[
            { title: 'ACOs Participating',    value: fmt.num(latest?.n_acos),
              delta: `${(latest?.n_acos - prior?.n_acos) > 0 ? '+' : ''}${latest?.n_acos - prior?.n_acos} vs PY ${prior?.performance_year}` },
            { title: <KPITooltip {...TOOLTIPS.earned_savings} />,  value: fmt.dollars(latest?.total_earn_save_loss, 2),
              delta: `+${fmt.dollars(latest?.total_earn_save_loss - prior?.total_earn_save_loss, 2)} vs PY ${prior?.performance_year}`, up: true },
            { title: <KPITooltip {...TOOLTIPS.pct_earning} />,  value: `${latest?.pct_earning_savings}%`,
              delta: `+${(latest?.pct_earning_savings - prior?.pct_earning_savings).toFixed(1)}pp vs PY ${prior?.performance_year}`, up: true },
            { title: <KPITooltip term='Program Savings Rate' def='(Σ Benchmark − Σ Expense) ÷ Σ Benchmark across all ACOs. A true program-level rate, not an average of individual ACO rates.' />, value: latestProgRate != null ? `${latestProgRate.toFixed(2)}%` : '—',
              delta: priorProgRate != null ? `${(latestProgRate - priorProgRate) > 0 ? '+' : ''}${(latestProgRate - priorProgRate).toFixed(2)}pp vs PY ${prior?.performance_year}` : '', up: true },
            { title: 'Assigned Beneficiaries', value: fmt.shortNum(latest?.total_n_ab),
              delta: `+${fmt.shortNum(latest?.total_n_ab - prior?.total_n_ab)} vs PY ${prior?.performance_year}` },
            { title: <KPITooltip {...TOOLTIPS.pbpm} />,      value: latest?.avg_expense_pbpm != null ? `$${latest.avg_expense_pbpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—',
              delta: `+$${(latest?.avg_expense_pbpm - prior?.avg_expense_pbpm).toFixed(2)} vs PY ${prior?.performance_year}` },
          ].map((k, ki) => (
            <div className="card" key={ki}>
              <div className="card-title">{k.title}</div>
              <div className={`card-value ${k.up ? 'positive' : ''}`}>{k.value}</div>
              {k.delta && <div className="card-delta up">{k.delta}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ── 2019A note ── */}
      <div className="note-banner">
        <span>⚠️</span>
        <span>
          <strong>2019 Pathways to Success:</strong> The main trend line reflects 475 legacy-track ACOs
          with a full 12-month performance period. A separate cohort of 205 new Pathways ACOs completed a
          6-month period (PY 2019A) and are excluded from year-over-year aggregates.{' '}
          {midyear[0] && (
            <span>The 2019A cohort earned <strong>{fmt.dollars(midyear[0].total_earn_save_loss, 2)}</strong> with
            a {(midyear[0].avg_sav_rate * 100).toFixed(2)}% average savings rate.</span>
          )}
        </span>
      </div>

      {/* ── Key Takeaways ── */}
      <div className="chart-card" style={{ background: 'var(--navy)', border: 'none' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase',
          letterSpacing: '0.1em', marginBottom: 10 }}>Key Takeaways · PY 2013–2024</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {[
            { icon: '📈', headline: `${fmt.dollars(allTimeStats?.totalGen, 2)} generated, ${fmt.dollars(allTimeStats?.totalEarned, 2)} earned`,
              body: `ACOs have generated ${fmt.dollars(allTimeStats?.totalGen, 2)} in gross savings since 2012. ACOs captured ${fmt.dollars(allTimeStats?.totalEarned, 2)}; CMS retained ${fmt.dollars(allTimeStats?.cmsRetained, 2)}, the split that LEAD\u2019s higher-risk tracks aim to shift toward providers.` },
            { icon: '🏆', headline: `${Math.round(latest?.pct_earning_savings)}% of ACOs earned savings in PY ${latest?.performance_year}`,
              body: `The share of ACOs earning savings has climbed from ${Math.round(legacy[0]?.pct_earning_savings)}% in ${legacy[0]?.performance_year} to ${Math.round(latest?.pct_earning_savings)}% in ${latest?.performance_year}, with a dip in 2021, reflecting both program maturation and a more experienced ACO population.` },
            { icon: '⏳', headline: 'Experience compounds over time',
              body: 'ACOs with 7+ years earn savings in 53–59% of performance years vs. 35–37% for new entrants. Average savings rates nearly double between early-stage and experienced ACOs.' },
            { icon: '🎯', headline: 'Risk track alignment leaves money on the table',
              body: 'Experienced one-sided ACOs captured just 38% of their generated savings in PY 2024 ($335M of $869M). Enhanced-track ACOs captured 73% — making track progression one of the highest-leverage decisions an ACO can make.' },
          ].map(t => (
            <div key={t.headline} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius)',
              padding: '12px 14px', borderLeft: '3px solid var(--teal)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'white', marginBottom: 4 }}>
                {t.icon} {t.headline}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{t.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Savings combo chart ── */}
      <ChartCard
        title="Total Savings: ACO Share vs. CMS Retained"
        sub="Generated savings split between ACO earned payments and CMS retained portion"
        filename="mssp-total-savings"
      >
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(1)+'B' : v+'M'}`} />
            <Tooltip content={<SavingsComboTooltip />} />
            <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="earn_M" name="ACO Earned" stackId="a" fill={COLORS.teal}   radius={[0,0,0,0]} />
            <Bar dataKey="cms_M"  name="CMS Retained" stackId="a" fill={COLORS.blue} radius={[3,3,0,0]} />
            <Scatter dataKey="gen_M" name="Total Generated" fill={COLORS.navy} shape="diamond" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── % Earning savings ── */}
      <div className="chart-card">
        <div className="chart-title">Percentage of ACOs Achieving Earned Savings</div>
        <div className="chart-sub">Share of ACOs that received a shared savings payment · 2013–2024</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="earningGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.teal} stopOpacity={0.25} />
                <stop offset="95%" stopColor={COLORS.teal} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} domain={[0, 85]} width={40} tickFormatter={v => `${v}%`} />
            <Tooltip content={<PctTooltip />} />
            <Area dataKey="pct_earning_savings" name="% Earning Savings"
              stroke={COLORS.teal} strokeWidth={2.5} fill="url(#earningGrad)"
              dot={{ r: 3.5, fill: COLORS.teal }} connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── ACO count + beneficiaries ── */}
      <div className="chart-card">
        <div className="chart-title">ACO Participation & Assigned Beneficiaries</div>
        <div className="chart-sub">Number of ACOs (left) and total assigned beneficiaries (right)</div>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 52, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} domain={[150, 600]} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={52}
              tickFormatter={v => fmt.shortNum(v)} />
            <Tooltip labelFormatter={l => `PY ${l}`}
              formatter={(v, name) => name === 'ACOs' ? [fmt.num(v), name] : [fmt.shortNum(v), name]} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="n_acos" name="ACOs" fill={COLORS.navy} opacity={0.85} radius={[3,3,0,0]} />
            <Line yAxisId="right" dataKey="total_n_ab" name="Assigned Beneficiaries"
              stroke={COLORS.teal} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Program-level savings rate ── */}
      <div className="chart-card">
        <div className="chart-title">Program-Level Savings Rate</div>
        <div className="chart-sub">
          (Σ Benchmark − Σ Expense) / Σ Benchmark across all ACOs — reflects true program savings, not average of ACO rates
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="savGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.2} />
                <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={v => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} width={46} />
            <Tooltip content={<PctTooltip />} />
            <ReferenceLine y={0} stroke={COLORS.gray} strokeWidth={1.5} />
            <Area dataKey="prog_sav_rate" name="Program Savings Rate"
              stroke={COLORS.green} strokeWidth={2.5} fill="url(#savGrad)"
              dot={{ r: 3.5, fill: COLORS.green }} connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Track distribution ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="chart-title">Track Distribution Over Time</div>
            <div className="chart-sub">
              {trackMode === 'detail'
                ? 'Actual track labels — Tracks 1/2/3 pre-2019; Basic A–E and Enhanced post-Pathways'
                : 'Simplified: one-sided vs. two-sided risk model'}
            </div>
          </div>
          <ToggleGroup value={trackMode} onChange={setTrackMode} options={[
            { value: 'risk', label: 'Risk model' },
            { value: 'detail', label: 'Actual tracks' },
          ]} />
        </div>
        <div className="note-banner" style={{ marginBottom: 12 }}>
          <span>📌</span>
          <span>Pathways to Success launched July 2019, replacing Tracks 1/2/3 with Basic A–E and Enhanced tracks.
            ACO REACH and other CMS programs operate under separate agreements and are not included here.</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={trackMode === 'detail' ? trackDetailData : trackRiskData}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={36} />
            <Tooltip labelFormatter={l => `PY ${l}`} />
            <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            {trackMode === 'detail'
              ? allDetailTracks.map(t => (
                  <Bar key={t} dataKey={t} stackId="a"
                    fill={TRACK_COLORS_DETAIL[t] || COLORS.gray} />
                ))
              : Object.entries(RISK_COLORS).map(([k, c]) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={c} />
                ))
            }
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Benchmark vs expense with toggle ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="chart-title">Benchmark vs. Actual Expense</div>
            <div className="chart-sub">The gap between lines is where program savings originate</div>
          </div>
          <ToggleGroup value={spendMode} onChange={setSpendMode} options={[
            { value: 'pbpm', label: 'PBPM' },
            { value: 'percapita', label: 'Per Beneficiary' },
            { value: 'aggregate', label: 'Aggregate' },
          ]} />
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={sk.yFmt} domain={['auto','auto']} />
            <Tooltip content={<SpendTooltip mode={spendMode} />} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <Line dataKey={sk.bnchmk} name="Benchmark" stroke={COLORS.navy}
              strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls />
            <Line dataKey={sk.exp} name="Actual Expense" stroke={COLORS.red}
              strokeWidth={2.5} dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Utilization: admissions + net ED + SNF ── */}
      <div className="chart-grid-2">
        <div className="chart-card">
          <div className="chart-title">Utilization Trends — Inpatient & ED</div>
          <div className="chart-sub">
            Per 1,000 beneficiary years · ED = non-hospitalization visits only (total ED minus ED → admit)
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip content={<UtilTooltip />} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Line dataKey="avg_adm"    name="IP Admissions"      stroke={COLORS.navy}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="avg_net_ed" name="ED Visits (non-hosp)" stroke={COLORS.coral} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="avg_p_snf_adm" name="SNF Admissions"  stroke={COLORS.amber} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <ReferenceLine x={2020} stroke="var(--gray-300)" strokeDasharray="4 2"
                label={{ value: 'COVID', position: 'top', fontSize: 10, fill: 'var(--gray-400)' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <div className="chart-title">E&M Visits by Provider Type</div>
          <div className="chart-sub">Per 1,000 beneficiary years — primary care, specialist, and APP visits</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={50} />
              <Tooltip content={<UtilTooltip />} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Line dataKey="avg_pcp_em"  name="PCP E&M"        stroke={COLORS.teal}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="avg_sp_em"   name="Specialist E&M" stroke={COLORS.navy}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="avg_nurse"   name="APP/Nurse Visits" stroke={COLORS.amber} strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Quality trend (moved from Quality Dashboard) ── */}
      <div className="chart-grid-2">
        <ChartCard title="Average Quality Score — Program Level"
          sub="Mean composite quality score across all ACOs · 2014–2024"
          filename="mssp-avg-quality-score">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData.filter(d => d.avg_qual != null)} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[75, 100]} width={36} />
              <Tooltip labelFormatter={l => `PY ${l}`} formatter={v => [v?.toFixed(1), 'Avg Quality Score']} />
              <ReferenceLine y={75} stroke="var(--gray-300)" strokeDasharray="4 2"
                label={{ value: 'QPS threshold', position: 'insideTopRight', fontSize: 10, fill: 'var(--gray-400)' }} />
              <Line dataKey="avg_qual" name="Avg Quality Score" stroke={COLORS.purple}
                strokeWidth={2.5} dot={{ r: 3.5, fill: COLORS.purple, stroke: 'white', strokeWidth: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="% ACOs Meeting Quality Performance Standard"
          sub="Share of ACOs meeting QPS threshold required for shared savings"
          filename="mssp-pct-meeting-qps">
          <div className="chart-sub">Share of ACOs meeting QPS threshold required for shared savings</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData.filter(d => d.pct_met_qps != null)} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[80, 101]} width={40} tickFormatter={v => `${v}%`} />
              <Tooltip labelFormatter={l => `PY ${l}`} formatter={v => [`${v?.toFixed(1)}%`, '% Met QPS']} />
              <Bar dataKey="pct_met_qps" name="% Met QPS" fill={COLORS.green} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Year-by-year timeline ── */}
      <div className="chart-card">
        <div className="chart-title" style={{ marginBottom: 20 }}>Year-by-Year Program Timeline</div>
        <div className="timeline">
          {legacy.map((d, idx) => {
            const yr    = d.performance_year;
            const isLast = idx === legacy.length - 1;
            const my    = midyear.find(m => m.performance_year === yr);
            return (
              <div className="timeline-item" key={yr}>
                <div className="timeline-year-col">
                  <div className="timeline-year">{yr}</div>
                </div>
                <div className="timeline-dot-col">
                  <div className="timeline-dot" />
                  {!isLast && <div className="timeline-dot-line" />}
                </div>
                <div className="timeline-content">
                  <div className="timeline-content-header">
                    <span className="timeline-event-name">
                      {PROGRAM_EVENTS[yr] || `Performance Year ${yr}`}
                    </span>
                    <span className="timeline-stat-pill pill-teal">{fmt.num(d.n_acos)} ACOs</span>
                    <span className={`timeline-stat-pill ${d.total_earn_save_loss > 0 ? 'pill-green' : 'pill-red'}`}>
                      {fmt.dollars(d.total_earn_save_loss, 2)} earned
                    </span>
                    {d.pct_earning_savings != null && (
                      <span className="timeline-stat-pill pill-blue">{d.pct_earning_savings}% earning savings</span>
                    )}
                  </div>
                  <p className="timeline-narrative">
                    {YEAR_NARRATIVES[yr]}
                    {my && (
                      <span style={{ display: 'block', marginTop: 4, color: 'var(--amber)', fontWeight: 500 }}>
                        + Pathways launch (2019A): {fmt.num(my.n_acos)} ACOs · 6-month period ·{' '}
                        {fmt.dollars(my.total_earn_save_loss, 2)} earned · {(my.avg_sav_rate * 100).toFixed(2)}% avg savings rate
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
